import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Tab,
    Tabs,
    TextField,
    Typography,
    List,
    ListItem,
    ListItemText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import useSupplementsData from "../hooks/useSupplementsData";
import { getAllActivityType } from "../../loanactivity/Service";
import { 
    getPortfolioAnalysisDetail, 
    getLoanServicingWorksheet1, 
    getLoanServicingWorksheet2
} from "../Service";

const FilterSupplement = () => {

    const loadScript = (src) => {
        return new Promise((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.onload = () => {
                console.log(`${src} loaded`);
                resolve();
            };
            script.onerror = (e) => {
                console.error(`Failed to load script ${src}`, e);
                reject(new Error(`Failed to load script: ${src}`));
            };
            document.body.appendChild(script);
        });
    };

    const waitForStimulsoft = async () => {
        if (window.Stimulsoft?.Viewer) {
            console.log('Stimulsoft already loaded');
            return window.Stimulsoft;
        }

        console.log('Loading Stimulsoft scripts...');
        await loadScript('/stimulsoft/stimulsoft.reports.js');
        await loadScript('/stimulsoft/stimulsoft.viewer.js');

        if (!window.Stimulsoft?.Viewer) {
            throw new Error('Stimulsoft failed to load properly.');
        }

        window.Stimulsoft.Base.StiLicense.key = "6vJhGtLLLz2GNviWmUTrhSqnOItdDwjBylQzQcAOiHk8RHJ6YukLSxFNxOsGywCmSfDmNx54ijoADYy8ZbVyM2jfqO" +
            "XKdON4rOS1c4pYt+vz3OZj2h5NQ5xt1EHgsdxT9FLudqeHLONw2Ilz5GFCADtzxp1LJgZzdOKAPwaCt2pKcALM8JCu" +
            "oSWTXhL6pSipVdAC3kmr4l/bdrNhEmf4ARCRcv8Je5e5c2/Jjol0t8ey/O5HrlXlz+EC2Cz4Dx66o6WvwzDMBv56zd" +
            "3cSiUMqyw5J9NVd3Dkk09nWGDipivUFGdO6p0xGmG+mXDOC+aap+URaMm3DKj2kWRXguqTyadS7r718J6UNqnuH5fG" +
            "/U0fnt5FumYLZmz+sPlmEo4tu3tDnAhGKcDj+ZIMG6h2YPo49FKTI/xrGWxUpVdA2z/1XqMN/LzY6eVUxOfLMuFNsZ" +
            "9anqLes7Xr5v3I2YWIfLeq60bMG0UbFYV0s7C7ybmH9Kvdnt7ddUbP0CWtZLNnbDxbRMayRP9ntwdQibsT5x6wJKA2" +
            "NPA7vDQwiKBmPVzZpBABwAAjeeztmBdxD/Rd";

        console.log('Stimulsoft loaded and license applied.');

        setTimeout(() => {
            console.log('License key:', window.Stimulsoft.Base.StiLicense.key);
            console.log('License status:', window.Stimulsoft.Base.StiLicense.status);
        }, 1000);
        return window.Stimulsoft;
    };

    const renderStimulsoftViewer = async (supplementdata) => {
        const Stimulsoft = await waitForStimulsoft();

        const options = new Stimulsoft.Viewer.StiViewerOptions();
        options.appearance.showBranding = false;
        options.appearance.showAboutButton = false;

        const viewer = new Stimulsoft.Viewer.StiViewer(options, 'StiViewer', false);
        const supplement = new Stimulsoft.Report.StiReport();

        supplement.loadDocument(supplementdata);

        viewer.report = supplement;
        viewer.renderHtml('viewerContent');
    };

    const {
        loannums,
        citys,
        loanofficers,
        borrowerTypeList,
        countys,
        loanTypes,
        loanStatusList,
        fundSources,
        fetchSupplementData,
    } = useSupplementsData();

    const [supplementName, setSupplementName] = useState('Year-To-Date Activity');
    const [beginDate, setBeginDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs());
    const [cutoffDate, setCutoffDate] = useState(dayjs());
    const [writeOffFromDate, setWriteOffFromDate] = useState(dayjs());
    const [writeOffToDate, setWriteOffToDate] = useState(dayjs());
    const [loanFrom, setLoanFrom] = useState('');
    const [loanTo, setLoanTo] = useState('');
    const [sortOption, setSortOption] = useState('# Loan Number');
    const [includeNotes, setIncludeNotes] = useState(true);
    const [selectedFunds, setSelectedFunds] = useState([]);
    const [selectedCounty, setSelectedCounty] = useState([]);
    const [selectedCity, setSelectedCity] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [selectedLoanOfficer, setSelectedLoanOfficer] = useState([]);
    const [selectedLoanType, setSelectedLoanType] = useState([]);
    const [selectedBorrower, setSelectedBorrower] = useState([]);
    const [activityTypes, setActivityTypes] = useState([]);
    const [selectedActivityType, setSelectedActivityType] = useState('All');
    const [reportType, setReportType] = useState('Detailed');
    const [tabValue, setTabValue] = useState(0);
    const [showViewer, setShowViewer] = useState(false);

    // Supplement names list
    const supplementNameOptions = [
        'Year-To-Date Activity',
        'Write-Off Analysis',
        'Portfolio Analysis',
        'Loan Servicing Worksheets'
    ];

    const fundOptions = fundSources.map(fs => fs.code + ' ' + fs.description);
    const sortOptions = ['A  Alphabetical', '# Loan Number'];
    const reportTypeOptions = ['Detailed', 'Summary'];
    const countyOptions = countys.map(c => c.county);
    const cityNames = citys.map(c => c.city);
    const statusOptions = loanStatusList.map(ls => ls.statusCode);
    const loanOfficerOptions = loanofficers.map(lo => lo.loanOfficer);
    const loanTypeOptions = loanTypes.map(lt => lt.code + ' ' + lt.description);
    const borrowerOptions = borrowerTypeList.map(b => b.code + ' ' + b.description);

    const isYearToDateActivity = supplementName === 'Year-To-Date Activity';
    const isPortfolioAnalysis = supplementName === 'Portfolio Analysis';
    const isWriteOffAnalysis = supplementName === 'Write-Off Analysis';
    const isLoanServicingWorksheets = supplementName === 'Loan Servicing Worksheets';

    // Fetch activity types on component mount
    useEffect(() => {
        const fetchActivityTypes = async () => {
            try {
                const response = await getAllActivityType();
                console.log('Activity Types API Response:', response);

                let apiActivityTypes = [];
                if (response.data) {
                    apiActivityTypes = Array.isArray(response.data)
                        ? response.data
                        : (response.data.activityTypes || []);
                }

                console.log('Parsed Activity Types:', apiActivityTypes);

                const activityTypeOptions = [
                    { id: 0, activityTypeName: 'All' },
                    ...apiActivityTypes
                ];

                setActivityTypes(activityTypeOptions);
            } catch (error) {
                console.error('Failed to fetch activity types', error);
                setActivityTypes([{ id: 0, activityTypeName: 'All' }]);
            }
        };

        fetchActivityTypes();
    }, []);

    const handleListSelectionChange = (value, list, setList) => {
        if (list.includes(value)) {
            setList(list.filter(item => item !== value));
        } else {
            setList([...list, value]);
        }
    };

    const handlePortfolioAnalysisDetail = async () => {
        console.log('=== Portfolio Analysis Detail Started ===');

        const payload = {
            supplementName,
            cutoffDate: cutoffDate ? cutoffDate.format('YYYY-MM-DD') : null,
            loanFrom: parseInt(loannums.find(x => x.id === loanFrom)?.loanNumber ?? "0"),
            loanTo: parseInt(loannums.find(x => x.id === loanTo)?.loanNumber ?? "0"),
            selectedFunds,
            selectedCounty,
            selectedCity,
            selectedStatus,
            selectedLoanOfficer,
            selectedLoanType,
            selectedBorrower,
        };

        console.log('Portfolio Analysis Detail Payload:', JSON.stringify(payload, null, 2));

        try {
            // Call API endpoint that returns Excel file
            const response = await getPortfolioAnalysisDetail(payload);

            // Create a download link and trigger download
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `PortfolioAnalysisDetail_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();

            // Cleanup
            link.remove();
            window.URL.revokeObjectURL(url);

            console.log('Portfolio Analysis Detail Excel downloaded successfully');
        } catch (error) {
            console.error('Error generating Portfolio Analysis Detail:', error);
            alert('Failed to generate Portfolio Analysis Detail. Check console for details.');
        }
    };

    const handleLoanServicingWorksheet1 = async () => {
        console.log('=== Loan Servicing Worksheet 1 Started ===');

        const payload = {
            supplementName,
            beginDate: beginDate ? beginDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            selectedFunds,
        };

        console.log('Loan Servicing Worksheet 1 Payload:', JSON.stringify(payload, null, 2));

        try {
            const response = await getLoanServicingWorksheet1(payload);

            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `LoanServicingWorksheet1_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

            console.log('Loan Servicing Worksheet 1 Excel downloaded successfully');
        } catch (error) {
            console.error('Error generating Loan Servicing Worksheet 1:', error);
            alert('Failed to generate Loan Servicing Worksheet 1. Check console for details.');
        }
    };

    const handleLoanServicingWorksheet2 = async () => {
        console.log('=== Loan Servicing Worksheet 2 Started ===');

        const payload = {
            supplementName,
            beginDate: beginDate ? beginDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            selectedFunds,
        };

        console.log('Loan Servicing Worksheet 2 Payload:', JSON.stringify(payload, null, 2));

        try {
            const response = await getLoanServicingWorksheet2(payload);

            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `LoanServicingWorksheet2_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);

            console.log('Loan Servicing Worksheet 2 Excel downloaded successfully');
        } catch (error) {
            console.error('Error generating Loan Servicing Worksheet 2:', error);
            alert('Failed to generate Loan Servicing Worksheet 2. Check console for details.');
        }
    };

    const handleGenerate = async () => {
        setShowViewer(true);
        console.log('=== Generate Supplement Started ===');
        console.log('Loan Numbers:', loannums);

        const payload = {
            supplementName,
            beginDate: beginDate ? beginDate.format('YYYY-MM-DD') : null,
            endDate: endDate ? endDate.format('YYYY-MM-DD') : null,
            cutoffDate: cutoffDate ? cutoffDate.format('YYYY-MM-DD') : null,
            writeOffFromDate: writeOffFromDate ? writeOffFromDate.format('YYYY-MM-DD') : null,
            writeOffToDate: writeOffToDate ? writeOffToDate.format('YYYY-MM-DD') : null,
            loanFrom: parseInt(loannums.find(x => x.id === loanFrom)?.loanNumber ?? "0"),
            loanTo: parseInt(loannums.find(x => x.id === loanTo)?.loanNumber ?? "0"),
            sortOption,
            reportType,
            includeNotes,
            selectedFunds,
            selectedCounty,
            selectedCity,
            selectedStatus,
            selectedLoanOfficer,
            selectedLoanType,
            selectedBorrower,
            activityType: selectedActivityType,
            tabValue,
            showViewer: true,
            loanMasterId: 0,
            loanActivityId: 0
        };

        console.log('API Payload:', JSON.stringify(payload, null, 2));

        try {
            const supplementdata = await fetchSupplementData(payload);
            console.log('Supplement data received, length:', supplementdata?.length || 0);
            const jsonSupplement = supplementdata;
            await renderStimulsoftViewer(jsonSupplement);
        } catch (error) {
            console.error('Error generating supplement:', error);
            alert('Failed to generate supplement. Check console for details.');
            setShowViewer(false);
        }
    };

    const tabPanels = [
        { label: 'Funds', options: fundOptions, selected: selectedFunds, setSelected: setSelectedFunds },
        { label: 'County', options: countyOptions, selected: selectedCounty, setSelected: setSelectedCounty },
        { label: 'City', options: cityNames, selected: selectedCity, setSelected: setSelectedCity },
        { label: 'Status', options: statusOptions, selected: selectedStatus, setSelected: setSelectedStatus },
        { label: 'Loan Officer', options: loanOfficerOptions, selected: selectedLoanOfficer, setSelected: setSelectedLoanOfficer },
        { label: 'Loan Type', options: loanTypeOptions, selected: selectedLoanType, setSelected: setSelectedLoanType },
        { label: 'Borrower', options: borrowerOptions, selected: selectedBorrower, setSelected: setSelectedBorrower },
    ];

    // Tab panels without Status for Write-Off Analysis
    const tabPanelsWithoutStatus = [
        { label: 'Funds', options: fundOptions, selected: selectedFunds, setSelected: setSelectedFunds },
        { label: 'County', options: countyOptions, selected: selectedCounty, setSelected: setSelectedCounty },
        { label: 'City', options: cityNames, selected: selectedCity, setSelected: setSelectedCity },
        { label: 'Loan Officer', options: loanOfficerOptions, selected: selectedLoanOfficer, setSelected: setSelectedLoanOfficer },
        { label: 'Loan Type', options: loanTypeOptions, selected: selectedLoanType, setSelected: setSelectedLoanType },
        { label: 'Borrower', options: borrowerOptions, selected: selectedBorrower, setSelected: setSelectedBorrower },
    ];

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                p: 2,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
            }}
        >
            <Card>
                <CardContent>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {!showViewer ? (
                            <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
                                <Box display="flex" gap={2} mb={2}>
                                    <FormControl fullWidth>
                                        <InputLabel>Supplement Name</InputLabel>
                                        <Select
                                            value={supplementName}
                                            onChange={e => setSupplementName(e.target.value)}
                                            label="Supplement Name"
                                        >
                                            {supplementNameOptions.map((name, idx) => (
                                                <MenuItem key={idx} value={name}>{name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                {isYearToDateActivity && (
                                    <>
                                        <Box display="flex" gap={2} mb={2}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="Begin Date"
                                                    value={beginDate}
                                                    onChange={(newValue) => setBeginDate(newValue)}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            </LocalizationProvider>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="End Date"
                                                    value={endDate}
                                                    onChange={(newValue) => setEndDate(newValue)}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            </LocalizationProvider>
                                        </Box>

                                        <Box display="flex" gap={2} mb={2}>
                                            <FormControl fullWidth>
                                                <InputLabel>Loan From</InputLabel>
                                                <Select
                                                    value={loanFrom}
                                                    onChange={e => setLoanFrom(e.target.value)}
                                                    label="Loan From"
                                                >
                                                    {loannums.map((loannum) => (
                                                        <MenuItem key={loannum.id} value={loannum.id}>
                                                            {loannum.loanNumber} {loannum.loanName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl fullWidth>
                                                <InputLabel>Loan To</InputLabel>
                                                <Select
                                                    value={loanTo}
                                                    onChange={e => setLoanTo(e.target.value)}
                                                    label="Loan To"
                                                >
                                                    {loannums.map((loannum) => (
                                                        <MenuItem key={loannum.id} value={loannum.id}>
                                                            {loannum.loanNumber} {loannum.loanName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Box display="flex" gap={2} mb={2}>
                                            <FormControl fullWidth>
                                                <InputLabel>Activity Type</InputLabel>
                                                <Select
                                                    value={selectedActivityType}
                                                    onChange={e => setSelectedActivityType(e.target.value)}
                                                    label="Activity Type"
                                                >
                                                    {activityTypes.map((activityType) => (
                                                        <MenuItem key={activityType.id} value={activityType.activityTypeName || activityType.name || 'All'}>
                                                            {activityType.activityTypeName || activityType.name || 'All'}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl fullWidth>
                                                <InputLabel>Report Type</InputLabel>
                                                <Select
                                                    value={reportType}
                                                    onChange={e => setReportType(e.target.value)}
                                                    label="Report Type"
                                                >
                                                    {reportTypeOptions.map((option) => (
                                                        <MenuItem key={option} value={option}>
                                                            {option}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Box display="flex" gap={2} mb={2}>
                                            <FormControl fullWidth>
                                                <InputLabel>Sort Options</InputLabel>
                                                <Select value={sortOption} onChange={e => setSortOption(e.target.value)} label="Sort Options">
                                                    {sortOptions.map((sortoption) => (
                                                        <MenuItem key={sortoption} value={sortoption}>
                                                            {sortoption}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Box display="flex" gap={2} mb={2}>
                                            <FormControlLabel
                                                control={<Checkbox checked={includeNotes} onChange={e => setIncludeNotes(e.target.checked)} />}
                                                label="Include Notes"
                                            />
                                        </Box>

                                        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                                            {tabPanels.map((tab, idx) => <Tab key={idx} label={tab.label} />)}
                                        </Tabs>

                                        <Box mt={2} sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
                                            <List>
                                                {tabPanels[tabValue].options.map((option, index) => (
                                                    <ListItem
                                                        key={index}
                                                        button
                                                        onClick={() => handleListSelectionChange(option, tabPanels[tabValue].selected, tabPanels[tabValue].setSelected)}
                                                        selected={tabPanels[tabValue].selected.includes(option)}
                                                    >
                                                        <ListItemText
                                                            primary={option}
                                                            secondary={tabPanels[tabValue].selected.includes(option) ? 'Selected' : ''}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    </>
                                )}

                                {/* Write-Off Analysis fields */}
                                {supplementName === 'Write-Off Analysis' && (
                                    <>
                                        <Box display="flex" gap={2} mb={2}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="Cutoff Date"
                                                    value={cutoffDate}
                                                    onChange={(newValue) => setCutoffDate(newValue)}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            </LocalizationProvider>
                                        </Box>

                                        <Box display="flex" gap={2} mb={2}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="Write-Off From Date"
                                                    value={writeOffFromDate}
                                                    onChange={(newValue) => setWriteOffFromDate(newValue)}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            </LocalizationProvider>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="Write-Off To Date"
                                                    value={writeOffToDate}
                                                    onChange={(newValue) => setWriteOffToDate(newValue)}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            </LocalizationProvider>
                                        </Box>

                                        <Box display="flex" gap={2} mb={2}>
                                            <FormControl fullWidth>
                                                <InputLabel>Sort Options</InputLabel>
                                                <Select value={sortOption} onChange={e => setSortOption(e.target.value)} label="Sort Options">
                                                    {sortOptions.map((sortoption) => (
                                                        <MenuItem key={sortoption} value={sortoption}>
                                                            {sortoption}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                                            {tabPanelsWithoutStatus.map((tab, idx) => <Tab key={idx} label={tab.label} />)}
                                        </Tabs>

                                        <Box mt={2} sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
                                            <List>
                                                {tabPanelsWithoutStatus[tabValue].options.map((option, index) => (
                                                    <ListItem
                                                        key={index}
                                                        button
                                                        onClick={() => handleListSelectionChange(option, tabPanelsWithoutStatus[tabValue].selected, tabPanelsWithoutStatus[tabValue].setSelected)}
                                                        selected={tabPanelsWithoutStatus[tabValue].selected.includes(option)}
                                                    >
                                                        <ListItemText
                                                            primary={option}
                                                            secondary={tabPanelsWithoutStatus[tabValue].selected.includes(option) ? 'Selected' : ''}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    </>
                                )}

                                {/* Portfolio Analysis fields */}
                                {supplementName === 'Portfolio Analysis' && (
                                    <>
                                        <Box display="flex" gap={2} mb={2}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="Cutoff Date"
                                                    value={cutoffDate}
                                                    onChange={(newValue) => setCutoffDate(newValue)}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            </LocalizationProvider>
                                        </Box>

                                        <Box display="flex" gap={2} mb={2}>
                                            <FormControl fullWidth>
                                                <InputLabel>Loan From</InputLabel>
                                                <Select
                                                    value={loanFrom}
                                                    onChange={e => setLoanFrom(e.target.value)}
                                                    label="Loan From"
                                                >
                                                    {loannums.map((loannum) => (
                                                        <MenuItem key={loannum.id} value={loannum.id}>
                                                            {loannum.loanNumber} {loannum.loanName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl fullWidth>
                                                <InputLabel>Loan To</InputLabel>
                                                <Select
                                                    value={loanTo}
                                                    onChange={e => setLoanTo(e.target.value)}
                                                    label="Loan To"
                                                >
                                                    {loannums.map((loannum) => (
                                                        <MenuItem key={loannum.id} value={loannum.id}>
                                                            {loannum.loanNumber} {loannum.loanName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>

                                        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                                            {tabPanels.map((tab, idx) => <Tab key={idx} label={tab.label} />)}
                                        </Tabs>

                                        <Box mt={2} sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
                                            <List>
                                                {tabPanels[tabValue].options.map((option, index) => (
                                                    <ListItem
                                                        key={index}
                                                        button
                                                        onClick={() => handleListSelectionChange(option, tabPanels[tabValue].selected, tabPanels[tabValue].setSelected)}
                                                        selected={tabPanels[tabValue].selected.includes(option)}
                                                    >
                                                        <ListItemText
                                                            primary={option}
                                                            secondary={tabPanels[tabValue].selected.includes(option) ? 'Selected' : ''}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    </>
                                )}

                                {/* Loan Servicing Worksheets fields */}
                                {isLoanServicingWorksheets && (
                                    <>
                                        <Box display="flex" gap={2} mb={2}>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="Begin Date"
                                                    value={beginDate}
                                                    onChange={(newValue) => setBeginDate(newValue)}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            </LocalizationProvider>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <DatePicker
                                                    label="End Date"
                                                    value={endDate}
                                                    onChange={(newValue) => setEndDate(newValue)}
                                                    slotProps={{ textField: { fullWidth: true } }}
                                                />
                                            </LocalizationProvider>
                                        </Box>

                                        <Typography variant="subtitle1" sx={{ mb: 1 }}>Select Funds</Typography>
                                        <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #ccc', borderRadius: 1, p: 1 }}>
                                            <List>
                                                {fundOptions.map((option, index) => (
                                                    <ListItem
                                                        key={index}
                                                        button
                                                        onClick={() => handleListSelectionChange(option, selectedFunds, setSelectedFunds)}
                                                        selected={selectedFunds.includes(option)}
                                                    >
                                                        <ListItemText
                                                            primary={option}
                                                            secondary={selectedFunds.includes(option) ? 'Selected' : ''}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    </>
                                )}

                            </Box>
                        ) : (
                            <Box>
                                <Box
                                    id="viewerContent"
                                    sx={{
                                        flexGrow: 1,
                                        overflow: 'auto',
                                        mx: 'auto',
                                        my: 2,
                                        width: '100%',
                                    }}
                                />
                                <Box sx={{ textAlign: 'center', mt: 2 }}>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => {
                                            setShowViewer(false);
                                            const viewerElement = document.getElementById('viewerContent');
                                            if (viewerElement) viewerElement.innerHTML = '';
                                        }}
                                    >
                                        Close
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {!showViewer && (
                        <Box sx={{ maxWidth: 710, mx: 'auto', mb: 2, textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center' }}>
                            {!isLoanServicingWorksheets && (
                                <Button variant="contained" onClick={handleGenerate}>
                                    Generate
                                </Button>
                            )}
                            {isPortfolioAnalysis && (
                                <Button variant="contained" color="primary" onClick={handlePortfolioAnalysisDetail}>
                                    Portfolio Analysis Detail
                                </Button>
                            )}
                            {isLoanServicingWorksheets && (
                                <>
                                    <Button variant="contained" color="primary" onClick={handleLoanServicingWorksheet1}>
                                        Worksheet1
                                    </Button>
                                    <Button variant="contained" color="primary" onClick={handleLoanServicingWorksheet2}>
                                        Worksheet2
                                    </Button>
                                </>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default FilterSupplement;
